import Button from '@carvana/ds-button';
import Spinner from '@carvana/ds-spinner';
import { icon } from '@carvana/ds-styles';
import { CircleAlert } from 'lucide-react';
import React from 'react';
import { EventedModal } from '|/app/modals';
import type { Summary } from '|/app/services';
import styles from './complete-purchase.module.css';
import type { CompletePurchaseStepProps } from './complete-purchase.types';
import { SimpleConfirm } from './components/simple-confirm';
import { AlertModalContent } from './components/slot-unavailable-modal/alert-modal-content';
import SlotUnavailableModal from './components/slot-unavailable-modal/slot-unavailable-modal';
import { PaymentProvider } from './contexts/payment';
import type { GetPaymentSummaryResponse } from './services/purchase-payments-api.types';
import useCompletePurchaseVm from './use-complete-purchase-vm';
import type { CompletePurchaseSubmitResult } from './use-complete-purchase-vm';

export interface CompletePurchasePriceDetailsRenderProps {
  paymentSummary: GetPaymentSummaryResponse;
  reviewSummary: Summary | null;
}

export interface CompletePurchasePayAtOrderRenderProps
  extends CompletePurchasePriceDetailsRenderProps {
  onSubmit: () => Promise<CompletePurchaseSubmitResult>;
  editPaymentMethod: () => void;
}

interface CompletePurchaseShellProps extends CompletePurchaseStepProps {
  renderPayAtOrder: (props: CompletePurchasePayAtOrderRenderProps) => React.ReactNode;
  renderPriceDetails?: (props: CompletePurchasePriceDetailsRenderProps) => React.ReactNode;
}

const CompletePurchaseInner: React.FC<CompletePurchaseShellProps> = ({
  renderPayAtOrder,
  renderPriceDetails,
  hasScheduleStep,
  editPaymentMethod,
  summary,
  ...restProps
}) => {
  const {
    mode,
    slotPreference,
    paymentSummary,
    isLoading,
    isSubmitting,
    error,
    isSlotUnavailableModalOpen,
    isDocUploadErrorModalOpen,
    handleSubmit,
    handleSelectNewTime,
    handleDismissDocUploadError
  } = useCompletePurchaseVm({
    hasScheduleStep,
    editPaymentMethod,
    summary,
    ...restProps
  });

  const docUploadErrorModal = (
    <EventedModal
      isOpen={isDocUploadErrorModalOpen}
      analyticsId="pp_checkout_doc_upload_error"
      zIndex={10000000}
      closeIcon={false}
      divLine={false}
      desktopWidth="480px"
      aria-labelledby="doc-upload-error-modal-title"
    >
      <AlertModalContent
        title="Something went wrong"
        titleId="doc-upload-error-modal-title"
        subtext={
          hasScheduleStep
            ? 'Your appointment is scheduled, but we had an issue submitting your order. Please try again.'
            : 'We had an issue submitting your order. Please try again.'
        }
        icon={<CircleAlert size={24} color={icon.warning} />}
        action={
          <Button
            variant="primary"
            width="100%"
            onClick={handleDismissDocUploadError}
            data-analytics-id="pp_checkout_doc_upload_error_dismiss"
          >
            Continue
          </Button>
        }
      />
    </EventedModal>
  );

  if (isSlotUnavailableModalOpen && mode === null) {
    return (
      <>
        <div className={styles.loadingContainer}>
          <p className={styles.unavailableText}>Time slot unavailable</p>
        </div>
        <SlotUnavailableModal
          isOpen
          displayDate={slotPreference?.displayDate ?? ''}
          displayTime={slotPreference?.displayTime ?? ''}
          onSelectNewTime={handleSelectNewTime}
        />
      </>
    );
  }

  if (isLoading || mode === null || (mode === 'pao' && paymentSummary === null)) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorText}>{error}</p>
      </div>
    );
  }

  if (mode === 'pao') {
    return (
      <>
        {renderPayAtOrder({
          onSubmit: handleSubmit,
          editPaymentMethod,
          paymentSummary,
          reviewSummary: summary ?? null
        })}
        <SlotUnavailableModal
          isOpen={isSlotUnavailableModalOpen}
          displayDate={slotPreference?.displayDate ?? ''}
          displayTime={slotPreference?.displayTime ?? ''}
          onSelectNewTime={handleSelectNewTime}
        />
        {docUploadErrorModal}
      </>
    );
  }

  return (
    <>
      <SimpleConfirm
        isSubmitting={isSubmitting}
        hasPaymentSummary={paymentSummary !== null}
        priceDetails={
          paymentSummary && renderPriceDetails?.({ paymentSummary, reviewSummary: summary ?? null })
        }
        onSubmit={() => {
          void handleSubmit();
        }}
      />
      <SlotUnavailableModal
        isOpen={isSlotUnavailableModalOpen}
        displayDate={slotPreference?.displayDate ?? ''}
        displayTime={slotPreference?.displayTime ?? ''}
        onSelectNewTime={handleSelectNewTime}
      />
      {docUploadErrorModal}
    </>
  );
};

const CompletePurchaseShell: React.FC<CompletePurchaseShellProps> = props => {
  return (
    <PaymentProvider>
      <div className={styles.container}>
        <CompletePurchaseInner {...props} />
      </div>
    </PaymentProvider>
  );
};

export default CompletePurchaseShell;
